import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { prepareImageForUpload } from '../lib/prepareImage'

const MAX_PHOTOS = 5

interface Props {
  idPrefix: string
  images: File[]
  onImages: (images: File[]) => void
  required?: boolean
  sightingGuidance?: boolean
  showRequiredError?: boolean
}

function fileKey(file: File) {
  return `${file.name}-${file.size}-${file.lastModified}`
}

function filesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return []

  const fromFiles = [...data.files].filter((file) => file.type.startsWith('image/'))
  if (fromFiles.length > 0) return fromFiles

  const fromItems: File[] = []
  for (const item of data.items) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (file) fromItems.push(file)
    }
  }
  return fromItems
}

function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

export function PhotoPicker({
  idPrefix,
  images,
  onImages,
  required = false,
  sightingGuidance = false,
  showRequiredError = false,
}: Props) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const imagesRef = useRef(images)
  const onImagesRef = useRef(onImages)
  const tRef = useRef(t)
  const preparingRef = useRef(false)
  const [previews, setPreviews] = useState<Array<{ file: File; url: string }>>([])
  const [preparing, setPreparing] = useState(false)
  const [prepareError, setPrepareError] = useState<string | null>(null)

  imagesRef.current = images
  onImagesRef.current = onImages
  tRef.current = t
  preparingRef.current = preparing

  useEffect(() => {
    const next = images.map((file) => ({ file, url: URL.createObjectURL(file) }))
    setPreviews(next)
    return () => next.forEach(({ url }) => URL.revokeObjectURL(url))
  }, [images])

  async function addImageFiles(candidates: File[]) {
    if (preparingRef.current || candidates.length === 0) return

    const current = imagesRef.current
    const remaining = MAX_PHOTOS - current.length
    if (remaining <= 0) return

    const selected = candidates
      .filter((file) => file.type.startsWith('image/'))
      .slice(0, remaining)

    if (selected.length === 0) return

    setPreparing(true)
    setPrepareError(null)
    try {
      const prepared: File[] = []
      for (const file of selected) {
        prepared.push(await prepareImageForUpload(file))
      }
      onImagesRef.current([...current, ...prepared].slice(0, MAX_PHOTOS))
    } catch {
      setPrepareError(tRef.current('forms.photoPrepareFailed'))
    } finally {
      setPreparing(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  useEffect(() => {
    function onPaste(event: ClipboardEvent) {
      if (preparingRef.current) return

      const pasted = filesFromClipboard(event.clipboardData)
      if (pasted.length === 0) return

      // Don't steal a text paste when the user is typing in a field.
      const text = event.clipboardData?.getData('text/plain')?.trim()
      if (text && isTextEntryTarget(event.target)) return

      event.preventDefault()
      void addImageFiles(pasted)
    }

    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
    // addImageFiles uses refs for latest images/callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <fieldset
      className={`photo-picker${showRequiredError ? ' has-error' : ''}`}
      aria-required={required}
    >
      <legend>
        {t('fields.photos')}
        {required && <span className="required-mark"> *</span>}
      </legend>
      <p className="photo-guidance">
        {sightingGuidance ? t('forms.sightingPhotosHelp') : t('forms.photosHelp')}
      </p>
      <p className="field-help">{t('forms.photoCompressHelp')}</p>
      {showRequiredError && (
        <p className="field-error" role="alert">
          {t('forms.photoRequired')}
        </p>
      )}
      {prepareError && (
        <p className="field-error" role="alert">
          {prepareError}
        </p>
      )}
      {preparing && (
        <p className="field-help" role="status">
          {t('forms.photoPreparing')}
        </p>
      )}

      <div className="photo-grid">
        {previews.map(({ file, url }, index) => (
          <figure className="photo-preview" key={fileKey(file)}>
            <img src={url} alt={t('forms.photoPreview', { number: index + 1 })} />
            <button
              type="button"
              className="photo-remove"
              onClick={() => onImages(images.filter((candidate) => candidate !== file))}
              aria-label={t('forms.removePhoto', { number: index + 1 })}
            >
              ×
            </button>
            <figcaption>{index + 1}</figcaption>
          </figure>
        ))}

        {images.length < MAX_PHOTOS && (
          <button
            type="button"
            className="photo-add"
            disabled={preparing}
            onClick={() => inputRef.current?.click()}
          >
            <span aria-hidden="true">＋</span>
            <strong>{t('forms.addPhotos')}</strong>
            <small>
              {t('forms.photoSlotsRemaining', { count: MAX_PHOTOS - images.length })}
            </small>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        id={`${idPrefix}-images`}
        className="visually-hidden"
        type="file"
        accept="image/*"
        multiple
        disabled={preparing}
        onChange={(event) => {
          const list = event.target.files
          if (!list) return
          void addImageFiles([...list])
        }}
      />
      <div className="photo-count">
        {t('forms.photoCount', { count: images.length, max: MAX_PHOTOS })}
      </div>
    </fieldset>
  )
}
